import React from "react";
import { Box, Container, VStack, HStack, Grid, Heading, Text } from "@chakra-ui/react";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Hero from "../components/common/Hero";
import Card from "../components/common/Card";
import CTAButton from "../components/common/CTAButton";
import { FaRocket, FaChartLine, FaCertificate, FaUsers, FaBriefcase, FaCheckCircle } from "react-icons/fa";

export default function ForStudents() {
  const learningPath = [
    {
      phase: "Phase 1: Foundations",
      duration: "4-6 weeks",
      description: "Master the fundamentals and core concepts of your chosen discipline.",
      activities: [
        "Theory and concepts",
        "Coding/technical basics",
        "Tools and environment setup",
        "Mini projects to reinforce learning",
      ],
    },
    {
      phase: "Phase 2: Real Projects",
      duration: "6-8 weeks",
      description: "Build production-grade projects that showcase your skills to employers.",
      activities: [
        "Real-world problem solving",
        "Code reviews from instructors",
        "Portfolio-building projects",
        "Peer collaboration",
      ],
    },
    {
      phase: "Phase 3: Interview Ready",
      duration: "4-6 weeks",
      description: "Prepare for interviews and earn your dream job.",
      activities: [
        "Mock interviews",
        "Resume optimization",
        "Salary negotiation",
        "LinkedIn optimization",
      ],
    },
  ];

  const benefits = [
    {
      icon: FaChartLine,
      title: "Industry-Ready Skills",
      description: "Learn what companies actually need. Not textbook theory - real job skills.",
    },
    {
      icon: FaBriefcase,
      title: "Real Projects",
      description: "Build a portfolio with 4-6 production-grade projects that get you interviews.",
    },
    {
      icon: FaUsers,
      title: "Expert Mentorship",
      description: "Learn from industry professionals with 5-10+ years of experience.",
    },
    {
      icon: FaCertificate,
      title: "Industry Certifications",
      description: "Earn recognized certifications that add value to your resume.",
    },
    {
      icon: FaRocket,
      title: "Career Placement",
      description: "85%+ placement within 3 months. We help you land your dream job.",
    },
    {
      icon: FaUsers,
      title: "Peer Community",
      description: "Join a cohort of motivated students. Network for life. Collaborate. Grow.",
    },
  ];

  const successMetrics = [
    {
      metric: "85%+",
      label: "Placement Rate",
      description: "Within 3 months of program completion",
    },
    {
      metric: "4.5L+",
      label: "Avg Starting Salary",
      description: "Competitive packages from top companies",
    },
    {
      metric: "92%",
      label: "Job Satisfaction",
      description: "Students actually enjoy their jobs",
    },
    {
      metric: "1000+",
      label: "Hiring Partners",
      description: "Network of companies actively hiring",
    },
  ];

  const studentStories = [
    {
      name: "Priya Sharma",
      role: "Full-Stack Engineer",
      company: "TCS",
      salary: "5.5L LPA",
      story:
        "I was struggling with web development. The real-project approach made all the difference. Now I'm leading a team at TCS.",
    },
    {
      name: "Ankit Kumar",
      role: "Data Scientist",
      company: "Flipkart",
      salary: "6.2L LPA",
      story:
        "The curriculum was perfectly aligned with what Flipkart was looking for. I landed my dream job in just 2 months.",
    },
    {
      name: "Maya Desai",
      role: "Mobile Developer",
      company: "Swiggy",
      salary: "5.8L LPA",
      story:
        "The projects I built during the program are what convinced Swiggy to hire me. Every project was meaningful and challenging.",
    },
  ];

  const whyChooseUs = [
    "Industry-ready curriculum (not just theory)",
    "Real projects that showcase your skills",
    "Expert mentors from top companies",
    "Career support (resume, interviews, negotiation)",
    "Flexible schedule (learn while you study)",
    "Peer community and networking",
    "Lifetime access to course materials",
    "Job guarantee (85%+ placed)",
  ];

  return (
    <Box>
      <Navbar />

      {/* Hero Section */}
      <Hero
        title="Your Path to a High-Paying Tech Career"
        subtitle="Get industry-ready skills, build an impressive portfolio, and *land* your dream job, *Straight* from college."
        badge="100% Practical, Job-Focused Learning"
        withGradient
      >
        <HStack spacing={4} justify="center" flexWrap="wrap">
          <CTAButton to="/disciplines" size="lg">
            Choose Your Discipline
          </CTAButton>
          <CTAButton to="/contact" variant="outline" size="lg">
            Talk to an Advisor
          </CTAButton>
        </HStack>
      </Hero>

      {/* The Opportunity Section */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Why Tech Skills Matter Right Now
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                India's tech industry is growing at 20% annually. Companies are desperately hiring skilled engineers. The question is: are YOU ready?
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={8} w="full">
              <Box p={8} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <Heading size="sm" color="#E74C3C" mb={4}>
                  The Problem
                </Heading>
                <VStack align="start" spacing={3}>
                  <HStack spacing={3}>
                    <Box color="#E74C3C">✗</Box>
                    <Text color="#475569">Most college education is outdated</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#E74C3C">✗</Box>
                    <Text color="#475569">Companies hire for skills you don't have</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#E74C3C">✗</Box>
                    <Text color="#475569">Coding bootcamps feel rushed</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#E74C3C">✗</Box>
                    <Text color="#475569">You don't know how to prepare for interviews</Text>
                  </HStack>
                </VStack>
              </Box>

              <Box p={8} bg="#ECFDF5" borderRadius="lg" border="1px solid #10B981">
                <Heading size="sm" color="#10B981" mb={4}>
                  Our Solution
                </Heading>
                <VStack align="start" spacing={3}>
                  <HStack spacing={3}>
                    <Box color="#10B981" fontSize="lg">
                      ✓
                    </Box>
                    <Text color="#475569">Learn exactly what companies are hiring for</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#10B981" fontSize="lg">
                      ✓
                    </Box>
                    <Text color="#475569">Build 4-6 projects that get you interviews</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#10B981" fontSize="lg">
                      ✓
                    </Box>
                    <Text color="#475569">Expert mentors + peer support</Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Box color="#10B981" fontSize="lg">
                      ✓
                    </Box>
                    <Text color="#475569">Interview prep + career coaching</Text>
                  </HStack>
                </VStack>
              </Box>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Success Metrics */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Our Track Record
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Don't just take our word for it. See the numbers.
              </Text>
            </VStack>

            <Grid
              templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }}
              gap={8}
              w="full"
            >
              {successMetrics.map((item, idx) => (
                <Box key={idx} p={6} bg="#F8FAFC" borderRadius="lg" border="1px solid #E2E8F0" textAlign="center">
                  <Box fontSize="3xl" fontWeight="bold" color="#10B981" mb={2}>
                    {item.metric}
                  </Box>
                  <Heading size="sm" color="#1E293B" mb={2}>
                    {item.label}
                  </Heading>
                  <Text fontSize="sm" color="#475569">
                    {item.description}
                  </Text>
                </Box>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Your Learning Journey */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Your Learning Journey
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                From zero to job-ready in 3-6 months. Here's what happens.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={8} w="full">
              {learningPath.map((item, idx) => (
                <Box key={idx} p={6} bg="white" borderRadius="lg" border="2px solid #10B981" position="relative">
                  <Box
                    position="absolute"
                    top={-4}
                    left={6}
                    bg="#10B981"
                    color="white"
                    px={4}
                    py={2}
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="bold"
                  >
                    {item.duration}
                  </Box>
                  <Heading size="md" color="#1E293B" mt={6} mb={2}>
                    {item.phase}
                  </Heading>
                  <Text color="#475569" mb={4}>
                    {item.description}
                  </Text>
                  <VStack align="start" spacing={2}>
                    {item.activities.map((activity, aidx) => (
                      <HStack key={aidx} spacing={2}>
                        <Box color="#10B981">
                          <FaCheckCircle />
                        </Box>
                        <Text fontSize="sm" color="#475569">
                          {activity}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Benefits Grid */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                What You Get
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Everything you need to become a job-ready engineer.
              </Text>
            </VStack>

            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
              gap={8}
              w="full"
            >
              {benefits.map((item, idx) => (
                <Card
                  key={idx}
                  icon={item.icon}
                  title={item.title}
                  description={item.description}
                />
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Why Choose Ojasa Mirai */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Why Choose Ojasa Mirai Over Alternatives?
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                We're different. Here's why students choose us.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6} w="full">
              {whyChooseUs.map((item, idx) => (
                <HStack key={idx} spacing={4} p={4} bg="white" borderRadius="lg">
                  <Box color="#10B981" fontSize="xl">
                    <FaCheckCircle />
                  </Box>
                  <Text color="#1E293B" fontWeight="500">
                    {item}
                  </Text>
                </HStack>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Student Success Stories */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Student Success Stories
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Real students. Real outcomes. Real careers.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={8} w="full">
              {studentStories.map((story, idx) => (
                <Box key={idx} p={6} bg="#F8FAFC" borderRadius="lg" border="1px solid #E2E8F0">
                  <Box mb={4}>
                    <Heading size="sm" color="#1E293B">
                      {story.name}
                    </Heading>
                    <Text color="#10B981" fontWeight="600" fontSize="sm">
                      {story.role} @ {story.company}
                    </Text>
                    <Text color="#475569" fontSize="sm">
                      {story.salary}
                    </Text>
                  </Box>
                  <Text color="#475569" italic>
                    "{story.story}"
                  </Text>
                </Box>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box
        bgGradient="linear(to-br, #0F172A, #1E293B, #10B981)"
        py={{ base: 12, md: 20 }}
        color="white"
      >
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading as="h2" size="2xl" color="white">
              Ready to Launch Your Tech Career?
            </Heading>
            <Text fontSize="lg" maxW="2xl" color="#FFFFFF">
              The best time to start was yesterday. The second best time is now. Join thousands of students building amazing careers.
            </Text>

            <HStack spacing={4} justify="center" flexWrap="wrap">
              <CTAButton to="/disciplines" size="lg">
                Explore Disciplines
              </CTAButton>
              <CTAButton to="/contact" variant="outline" size="lg">
                Schedule Free Consultation
              </CTAButton>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
